import clsx from "clsx";
import React, { useLayoutEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";

import { Box, BoxProps, Typography } from "@mui/material";
import { Tooltip } from "@shared/Tooltip";

const useStyles = makeStyles()(() => ({
    hasBorder: {
        borderBottom: "1px solid lightgray",
    },
    flex: { display: "flex", alignItems: "center" },
    propertyKeySpan: {
        flex: "0 0 25.5%",
    },
    propertyValueSpan: {
        flex: 1,
    },
    overflowEllipsis: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        minWidth: 0,
        verticalAlign: "middle",
        display: "inline-block",
    },
}));

export function PropertyKey({ children }: React.ComponentProps<"span">): React.JSX.Element {
    const { classes } = useStyles();
    const label = typeof children === "string" ? children : String(children);
    const spanRef = useRef<HTMLSpanElement>(null);
    const [isOverflowed, setIsOverflowed] = useState(false);

    useLayoutEffect(() => {
        const checkOverflow = () => {
            const el = spanRef.current;
            if (el) {
                setIsOverflowed(el.scrollWidth > el.clientWidth);
            }
        };

        checkOverflow();

        // Re-check on window resize
        window.addEventListener("resize", checkOverflow);
        return () => window.removeEventListener("resize", checkOverflow);
    }, [children, spanRef]);

    return (
        <Tooltip title={label} placement="top" hide={!isOverflowed} disableChildrenWrapper>
            <Typography
                ref={spanRef}
                component="span"
                variant="body1"
                className={clsx(classes.propertyKeySpan, classes.overflowEllipsis)}
            >
                {children}
            </Typography>
        </Tooltip>
    );
}

interface PropertyRowProps extends BoxProps {
    hasBorder?: boolean;
}
export function PropertyRow({
    children,
    hasBorder = false,
    style,
}: React.ComponentProps<typeof Box> & PropertyRowProps): React.JSX.Element {
    const { classes } = useStyles();

    return (
        <Box style={style} className={clsx({ [classes.hasBorder]: hasBorder }, classes.flex)}>
            {children}
        </Box>
    );
}

export const PropertyValue = (props: React.ComponentProps<"span">) => {
    const { classes } = useStyles();
    return (
        <Typography
            component="span"
            variant="body1"
            {...props}
            className={clsx(classes.propertyValueSpan, classes.overflowEllipsis, props.className)}
        >
            {props.children}
        </Typography>
    );
};
